/* eslint-disable */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { exportExcel, exportPDF, exportJSON, loadJSZip, fetchImageBlob, downloadZip } from "./utils";
import { ExportFormatCard } from "./ExportFormatCard";
import { ExportItemRow } from "./ExportItemRow";
import styles from "./ExportModal.module.css";

const ICON_DL   = <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>;
const ICON_X    = <svg viewBox="0 0 24 24"><path d="M18 6 6 18M6 6l12 12" /></svg>;
const I_XLS  = <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 3v18" /></svg>;
const I_PDF  = <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M9 13h6M9 17h4" /></svg>;
const I_JSON = <svg viewBox="0 0 24 24"><path d="M8 3H7a2 2 0 0 0-2 2v4a2 2 0 0 1-2 2 2 2 0 0 1 2 2v4a2 2 0 0 0 2 2h1M16 3h1a2 2 0 0 1 2 2v4a2 2 0 0 0 2 2 2 2 0 0 0-2 2v4a2 2 0 0 1-2 2h-1" /></svg>;
const I_IMG  = <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-5-5L5 21" /></svg>;

const FORMATS = [
  { id: "excel", name: "Excel / CSV", desc: "Spreadsheet of all fields", icon: I_XLS },
  { id: "pdf",   name: "PDF",         desc: "Printable A4 catalog sheets", icon: I_PDF },
  { id: "json",  name: "JSON + Images", desc: "Full backup with image URLs", icon: I_JSON },
  { id: "images", name: "Images by brand", desc: "ZIP, one folder per brand", icon: I_IMG },
];

const C_GREEN = "#3a8845", C_GREY = "#8e887b", C_BLUE = "#3f72a4", C_AMBER = "#a86810";
function statusColor(s) {
  const t = (s || "").toLowerCase();
  if (t.includes("develop")) return C_GREEN;
  if (t.includes("archiv")) return C_GREY;
  if (t.includes("office")) return C_BLUE;
  if (t.includes("dev") || t.includes("progress") || t.includes("selection") || t.includes("order")) return C_AMBER;
  return C_GREY;
}

// Shell v3 (CLB-012 S3): `preselect` — a Set of item ids ticked on open (the
// Originals bulk bar's Export); absent → every item, as before.
export function ExportModal({ onClose, showToast, items, fields, fetchAllItemsRaw, T, cardSt, btnP, btnS, br, br75, preselect = null }) {
  const tick = (list) => new Set(preselect && preselect.size ? list.filter(i => preselect.has(i.id)).map(i => i.id) : list.map(i => i.id));
  const [exportItems, setExportItems] = useState([]);
  const [exportSel, setExportSel] = useState(new Set());
  const [exportTab, setExportTab] = useState("excel");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [includeArchived, setIncludeArchived] = useState(true);
  const [groupByBrand, setGroupByBrand] = useState(false);
  const [catalogDownloadBrands, setCatalogDownloadBrands] = useState([]);
  const [catalogDownloadScope, setCatalogDownloadScope] = useState("all");
  const [catalogDownloading, setCatalogDownloading] = useState(false);
  const [catalogDownloadProgress, setCatalogDownloadProgress] = useState("");
  const [loadingAll, setLoadingAll] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);

  useEffect(() => {
    // app-shell 2026-09-14: the page is the immediate list; the export always fetches every full row itself
    const immediate = items;
    if (immediate.length > 0) { setExportItems(immediate); setExportSel(tick(immediate)); }
    let cancelled = false;
    (async () => {
      setLoadingAll(true); setLoadedCount(immediate.length);
      try {
        const all = await fetchAllItemsRaw((loaded) => { if (!cancelled) setLoadedCount(loaded); });
        if (!cancelled && all.length > 0) { setExportItems(all); setExportSel(tick(all)); }
      } catch (e) { console.error("ExportModal load error:", e); }
      if (!cancelled) setLoadingAll(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const doExport = () => {
    const toExport = exportItems.filter(i => exportSel.has(i.id));
    if (!toExport.length) { showToast("Select at least one item", true); return; }
    if (exportTab === "pdf") exportPDF(toExport, fields);
    else if (exportTab === "json") exportJSON(toExport);
    else exportExcel(toExport, fields);
    onClose();
    showToast(`Exported ${toExport.length} items as ${exportTab.toUpperCase()}`);
  };

  const brands = useMemo(() => [...new Set(exportItems.map(i => i.brand).filter(Boolean))], [exportItems]);
  const statusCounts = useMemo(() => {
    const m = new Map();
    exportItems.forEach(i => { const s = i.status || "—"; m.set(s, (m.get(s) || 0) + 1); });
    return [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [exportItems]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return exportItems.filter(i => {
      if (statusFilter !== "all" && (i.status || "—") !== statusFilter) return false;
      if (q && !`${i.model || ""} ${i.brand || ""} ${i.sku || ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [exportItems, search, statusFilter]);

  const toggle = useCallback((id) => setExportSel(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; }), []);
  const date = new Date().toISOString().slice(0, 10);
  const fmt = FORMATS.find(f => f.id === exportTab);
  const subLine = exportTab === "excel" ? `${fields.length} columns · CSV · carolina_lemke_catalog_${date}.csv`
    : exportTab === "pdf" ? "A4 landscape · opens print sheet"
    : `full backup · catalog_${date}.json`;

  const runImages = async () => {
    setCatalogDownloading(true);
    try {
      const JSZip = await loadJSZip();
      const zip = new JSZip();
      const scopeItems = catalogDownloadScope === "all" ? exportItems : exportItems.filter(i => catalogDownloadBrands.includes(i.brand));
      const byBrand = {};
      scopeItems.forEach(item => { const b = (item.brand || "Other").replace(/[^a-zA-Z0-9_\- ]/g, "_"); (byBrand[b] = byBrand[b] || []).push(item); });
      let done = 0;
      for (const [brand, brandItems] of Object.entries(byBrand)) {
        const folder = zip.folder(brand);
        for (const item of brandItems) {
          const imgs = item.images || [];
          const sku = (item.sku || item.id || "item").replace(/[^a-zA-Z0-9_\-]/g, "_");
          for (let j = 0; j < imgs.length; j++) { const blob = await fetchImageBlob(imgs[j]); if (blob) folder.file(`${sku}_${j + 1}.jpg`, blob); }
          done++; setCatalogDownloadProgress(`${done} / ${scopeItems.length} items…`);
        }
      }
      setCatalogDownloadProgress("Creating ZIP…");
      await downloadZip(zip, `catalog_images_${date}.zip`);
      onClose();
    } catch (e) { console.error(e); }
    setCatalogDownloading(false); setCatalogDownloadProgress("");
  };
  const imgScopeItems = catalogDownloadScope === "all" ? exportItems : exportItems.filter(i => catalogDownloadBrands.includes(i.brand));
  const imgDisabled = catalogDownloading || (catalogDownloadScope === "select" && catalogDownloadBrands.length === 0);

  return (
    <div className={styles.exportRoot} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.head}>
          <span className={styles.headBadge}>{ICON_DL}</span>
          <div className={styles.headText}>
            <div className={styles.title}>Export Originals</div>
            <div className={styles.stat}>{exportItems.length} models · {brands.length} brands · {statusCounts.length} statuses</div>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Close">{ICON_X}</button>
        </div>

        <div className={styles.body}>
          {/* LEFT — format + options */}
          <div className={styles.left}>
            <div className={styles.eyebrow}>Export format</div>
            <div className={styles.fmtGrid}>
              {FORMATS.map(f => <ExportFormatCard key={f.id} fmt={f} active={exportTab === f.id} onClick={() => setExportTab(f.id)} />)}
            </div>
            <div className={styles.detail}>
              {exportTab === "excel" && (
                <>
                  <div className={styles.detailLabel}>Included columns · {fields.length}</div>
                  <div className={styles.chipCloud}>{fields.map(f => <span key={f.key} className={styles.chip}>{f.label}</span>)}</div>
                  <div className={styles.switchRow}>
                    <span className={styles.switchLabel}>Include archived</span>
                    <button className={`${styles.switch} ${includeArchived ? styles.switchOn : ""}`} onClick={() => setIncludeArchived(v => !v)} aria-pressed={includeArchived} />
                  </div>
                  <div className={styles.switchRow}>
                    <span className={styles.switchLabel}>Group rows by brand</span>
                    <button className={`${styles.switch} ${groupByBrand ? styles.switchOn : ""}`} onClick={() => setGroupByBrand(v => !v)} aria-pressed={groupByBrand} />
                  </div>
                </>
              )}
              {exportTab === "pdf" && <div className={styles.note}>Renders <strong>A4 landscape</strong> sheets with the full field table and a Carolina Lemke Berlin header. Opens a print-ready window.</div>}
              {exportTab === "json" && <div className={styles.note}>A <strong>full backup</strong> — every field plus image URLs, wrapped with export metadata. Best for re-import.</div>}
              {exportTab === "images" && <div className={styles.note}>A <strong>ZIP</strong> with one folder per brand · {brands.length} folders. File name = SKU barcode.</div>}
            </div>
          </div>

          {/* RIGHT — selection or brand scope */}
          {exportTab === "images" ? (
            <div className={styles.imgPane}>
              {["all", "select"].map(opt => (
                <label key={opt} className={`${styles.radioRow} ${catalogDownloadScope === opt ? styles.radioRowOn : ""}`}>
                  <input type="radio" name="catscope2" checked={catalogDownloadScope === opt} onChange={() => setCatalogDownloadScope(opt)} />
                  {opt === "all" ? `All brands (${exportItems.length} items)` : "Select specific brands"}
                </label>
              ))}
              {catalogDownloadScope === "select" && (
                <>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className={styles.selBtn} onClick={() => setCatalogDownloadBrands(brands)}>All</button>
                    <button className={styles.clearBtn} onClick={() => setCatalogDownloadBrands([])}>Clear</button>
                  </div>
                  <div className={styles.brandList}>
                    {brands.slice().sort().map(brand => {
                      const cnt = exportItems.filter(i => i.brand === brand).length;
                      const checked = catalogDownloadBrands.includes(brand);
                      return (
                        <label key={brand} className={`${styles.radioRow} ${checked ? styles.radioRowOn : ""}`}>
                          <input type="checkbox" checked={checked} onChange={e => setCatalogDownloadBrands(p => e.target.checked ? [...p, brand] : p.filter(x => x !== brand))} />
                          <span style={{ flex: 1 }}>{brand}</span>
                          <span className={styles.statusCount}>{cnt}</span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className={styles.right}>
              <div className={styles.rTools}>
                <input className={styles.search} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search models, brands, SKU…" />
                <button className={styles.selBtn} onClick={() => setExportSel(new Set(visible.map(i => i.id)))}>Select all</button>
                <button className={styles.clearBtn} onClick={() => setExportSel(new Set())}>Clear</button>
              </div>
              <div className={styles.statusStrip}>
                <button className={`${styles.statusPill} ${statusFilter === "all" ? styles.statusPillActive : ""}`} onClick={() => setStatusFilter("all")}>
                  All <span className={styles.statusCount}>{exportItems.length}</span>
                </button>
                {statusCounts.map(([s, cnt]) => (
                  <button key={s} className={`${styles.statusPill} ${statusFilter === s ? styles.statusPillActive : ""}`} onClick={() => setStatusFilter(s)}>
                    <span className={styles.statusDot} style={{ background: statusColor(s) }} />{s} <span className={styles.statusCount}>{cnt}</span>
                  </button>
                ))}
              </div>
              <div className={styles.list}>
                {exportItems.length === 0 && <div className={styles.listEmpty}>Loading items…</div>}
                {loadingAll && <div className={styles.loadNote}>Loading all items… {loadedCount} loaded</div>}
                {visible.map(item => (
                  <ExportItemRow key={item.id} item={item} selected={exportSel.has(item.id)} onToggle={() => toggle(item.id)} statusColor={statusColor} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className={styles.foot}>
          {exportTab === "images" ? (
            <>
              <div className={styles.summary}>
                <span className={styles.sumIcon}>{I_IMG}</span>
                <div>
                  <div className={styles.sumText}>Exporting <strong>{imgScopeItems.length}</strong> originals as ZIP</div>
                  <div className={styles.sumSub}>{catalogDownloadScope === "all" ? brands.length : catalogDownloadBrands.length} brand folders · catalog_images_{date}.zip</div>
                </div>
              </div>
              <button className={styles.btnGhost} onClick={onClose}>Cancel</button>
              <button className={styles.btnExport} disabled={imgDisabled} onClick={runImages}>
                {ICON_DL}{catalogDownloading ? (catalogDownloadProgress || "Working…") : "Download ZIP"}
              </button>
            </>
          ) : (
            <>
              <div className={styles.summary}>
                <span className={styles.sumIcon}>{ICON_DL}</span>
                <div>
                  <div className={styles.sumText}>Exporting <strong>{exportSel.size}</strong> originals as {fmt?.name}</div>
                  <div className={styles.sumSub}>{subLine}</div>
                </div>
              </div>
              <button className={styles.btnGhost} onClick={onClose}>Cancel</button>
              <button className={styles.btnExport} disabled={exportSel.size === 0} onClick={doExport}>
                {ICON_DL}Export <span className={styles.btnCount}>{exportSel.size || ""}</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
